import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Paper,
  Typography,
  Grid,
  InputAdornment,
  IconButton,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
  Check as CheckIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { API_CONFIG } from '../../config/apiConfig';
import { SignupData } from '../../pages/Auth/SignupPage';
import TermsCheckbox from './TermsCheckbox';

interface SignupAccountStepProps {
  data: SignupData;
  onUpdate: (data: Partial<SignupData>) => void;
  onNext: () => void;
}

interface PasswordStrength {
  score: number;
  feedback: {
    warning?: string;
    suggestions?: string[];
  };
}

const organizationTypes = [
  { value: 'startup', label: 'Startup', description: 'Early-stage company' },
  { value: 'small_business', label: 'Small Business', description: 'Established SMB' },
  { value: 'enterprise', label: 'Enterprise', description: 'Large corporation' },
  { value: 'educational', label: 'Educational', description: 'School or university' },
];

const industries = [
  'Technology',
  'Healthcare',
  'Finance',
  'Manufacturing',
  'Retail',
  'Education',
  'Consulting',
  'Real Estate',
  'Media & Entertainment',
  'Non-profit',
  'Government',
  'Other',
];

const teamSizes = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '500+', label: '500+ employees' },
];

export const SignupAccountStep: React.FC<SignupAccountStepProps> = ({
  data,
  onUpdate,
  onNext,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength | null>(null);
  const [checkingPassword, setCheckingPassword] = useState(false);
  const [termsError, setTermsError] = useState(false);

  // Check password strength
  useEffect(() => {
    const checkPasswordStrength = async () => {
      if (!data.password) {
        setPasswordStrength(null);
        return;
      }

      setCheckingPassword(true);
      try {
        const response = await fetch(`${API_CONFIG.USER_SERVICE_URL}/api/v1/auth/password-strength`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ password: data.password }),
        });

        if (response.ok) {
          const result = await response.json();
          setPasswordStrength(result.data.strength);
        }
      } catch (error) {
        console.error('Failed to check password strength:', error);
      } finally {
        setCheckingPassword(false);
      }
    };

    // Debounce the password strength check
    const timeoutId = setTimeout(checkPasswordStrength, 300);
    return () => clearTimeout(timeoutId);
  }, [data.password]);

  const getPasswordStrengthLabel = (score: number): string => {
    switch (score) {
      case 0:
        return 'Weak';
      case 1:
        return 'Fair';
      case 2:
        return 'Good';
      case 3:
        return 'Strong';
      case 4:
        return 'Very Strong';
      default:
        return 'Unknown';
    }
  };

  const getPasswordStrengthColor = (score: number): string => {
    switch (score) {
      case 0:
        return '#d32f2f'; // red
      case 1:
        return '#f57c00'; // orange
      case 2:
        return '#fbc02d'; // yellow
      case 3:
        return '#388e3c'; // green
      case 4:
        return '#1976d2'; // blue
      default:
        return '#9e9e9e'; // grey
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!data.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!data.password) {
      newErrors.password = 'Password is required';
    } else if (data.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (passwordStrength && passwordStrength.score < 2) {
      newErrors.password = 'Password is too weak. Please choose a stronger password';
    }

    if (!data.firstName) {
      newErrors.firstName = 'First name is required';
    }

    if (!data.lastName) {
      newErrors.lastName = 'Last name is required';
    }

    if (!data.organizationName) {
      newErrors.organizationName = 'Organization name is required';
    } else if (data.organizationName.length < 2) {
      newErrors.organizationName = 'Organization name must be at least 2 characters';
    }

    if (!data.organizationType) {
      newErrors.organizationType = 'Organization type is required';
    }

    if (!data.industry) {
      newErrors.industry = 'Industry is required';
    }

    if (!data.teamSize) {
      newErrors.teamSize = 'Team size is required';
    }

    if (!data.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
      setTermsError(true);
    } else {
      setTermsError(false);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onNext();
    }
  };

  const handleChange = (field: keyof SignupData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    onUpdate({ [field]: event.target.value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <Paper elevation={3} style={{ padding: 24, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" style={{ marginBottom: 16, fontSize: '1.1rem' }}>
        Create Your Account & Organization
      </Typography>

      <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={1} style={{ flex: 1, overflowY: 'auto', paddingRight: 8 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="First Name"
              value={data.firstName}
              onChange={handleChange('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Last Name"
              value={data.lastName}
              onChange={handleChange('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Email Address"
              type="email"
              value={data.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={data.password}
              onChange={handleChange('password')}
              error={!!errors.password}
              helperText={errors.password}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Compact Password Requirements */}
            <div style={{ marginTop: 8, display: 'flex', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {data.password.length >= 8 ? (
                  <CheckIcon fontSize="small" color="success" />
                ) : (
                  <CloseIcon fontSize="small" color="error" />
                )}
                <Typography variant="caption" style={{ fontSize: '0.8rem' }}>8+ chars</Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {passwordStrength && passwordStrength.score >= 2 ? (
                  <CheckIcon fontSize="small" color="success" />
                ) : (
                  <CloseIcon fontSize="small" color="error" />
                )}
                <Typography variant="caption" style={{ fontSize: '0.8rem' }}>Not common</Typography>
              </div>
            </div>

            {/* Compact Password Strength Indicator */}
            {data.password && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Typography variant="caption" style={{ fontSize: '0.8rem' }}>
                    Strength:
                  </Typography>
                  {!checkingPassword && passwordStrength && (
                    <Typography
                      variant="caption"
                      style={{
                        fontSize: '0.8rem',
                        color: getPasswordStrengthColor(passwordStrength.score),
                        fontWeight: 500
                      }}
                    >
                      {getPasswordStrengthLabel(passwordStrength.score)}
                    </Typography>
                  )}
                </div>
                <LinearProgress
                  variant={checkingPassword ? 'indeterminate' : 'determinate'}
                  value={passwordStrength ? (passwordStrength.score + 1) * 20 : 0}
                  style={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: '#e0e0e0',
                  }}
                />
              </div>
            )}
          </Grid>

          {/* Organization Section */}
          <Grid item xs={12}>
            <Divider style={{ marginTop: 8, marginBottom: 8 }}>
              <Typography variant="caption" color="text.secondary" style={{ fontSize: '0.85rem' }}>
                Organization Details
              </Typography>
            </Divider>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Organization Name"
              value={data.organizationName}
              onChange={handleChange('organizationName')}
              error={!!errors.organizationName}
              helperText={errors.organizationName}
              required
              placeholder="e.g., Acme Corporation"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.organizationType} required size="small">
              <InputLabel id="org-type-label">Organization Type</InputLabel>
              <Select
                labelId="org-type-label"
                id="org-type"
                value={data.organizationType}
                label="Organization Type"
                onChange={handleChange('organizationType')}
                size="small"
              >
                {organizationTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.organizationType && (
                <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block', fontSize: '0.75rem' }}>
                  {errors.organizationType}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.teamSize} required size="small">
              <InputLabel id="team-size-label">Team Size</InputLabel>
              <Select
                labelId="team-size-label"
                id="team-size"
                value={data.teamSize}
                label="Team Size"
                onChange={handleChange('teamSize')}
                size="small"
                startAdornment={
                  <InputAdornment position="start">
                    <GroupIcon color="action" fontSize="small" style={{ marginLeft: 12 }} />
                  </InputAdornment>
                }
              >
                {teamSizes.map((size) => (
                  <MenuItem key={size.value} value={size.value}>
                    {size.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.teamSize && (
                <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block', fontSize: '0.75rem' }}>
                  {errors.teamSize}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.industry} required size="small">
              <InputLabel id="industry-label">Industry</InputLabel>
              <Select
                labelId="industry-label"
                id="industry"
                value={data.industry}
                label="Industry"
                onChange={handleChange('industry')}
                size="small"
                startAdornment={
                  <InputAdornment position="start">
                    <CategoryIcon color="action" fontSize="small" style={{ marginLeft: 12 }} />
                  </InputAdornment>
                }
              >
                {industries.map((industry) => (
                  <MenuItem key={industry} value={industry}>
                    {industry}
                  </MenuItem>
                ))}
              </Select>
              {errors.industry && (
                <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block', fontSize: '0.75rem' }}>
                  {errors.industry}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Terms and Conditions */}
          <Grid item xs={12}>
            <TermsCheckbox
              checked={data.acceptTerms}
              onChange={(checked) => {
                onUpdate({ acceptTerms: checked });
                if (checked && termsError) {
                  setTermsError(false);
                  setErrors({ ...errors, acceptTerms: '' });
                }
              }}
              error={termsError}
              helperText={errors.acceptTerms}
              variant="signup"
            />
          </Grid>
        </Grid>

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" style={{ maxWidth: '60%', fontSize: '0.85rem' }}>
            Enterprise-grade security with MFA
          </Typography>
          <Button
            type="submit"
            variant="contained"
            size="medium"
            style={{ paddingLeft: 24, paddingRight: 24, fontSize: '0.9rem' }}
          >
            Next: Choose Your Plan
          </Button>
        </div>
      </form>
    </Paper>
  );
};